import { useEffect, useState } from "react";
import {
  useGetCreativeUserQuery
} from "../../api/creative";
import { CreativeType } from "../../types/types";
import { useDispatch, useSelector } from "react-redux";
import { ButtonV1 } from "../atoms/button";
import {
  storeCreativeCleanHandler,
  storeHandler,
} from "../../api/store/storeActions";
import { Loader } from "../atoms/loader";
import { UserProductCard } from "../molecules/userCards";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";

interface item {
  _id: string;
  title: string;
}

interface itemSchema {
  stage: string;
  title: string;
  thumbLink: string;
  videoLink: string;
  dt: string;
  orientation: string;
  mediaType: string;
  imageGallery: string[];
  pptDetails: {
    pptLink: string;
    pdfLink: string;
  };
  _id: string;
}
interface props {
  setLength: Function;
  length: number | undefined;
  regex: string;
  sortType: string;
  sortOrder: Number;
  pageNo: number;
  setPageNo: Function;
  isLoading: boolean;
  setIsLoading: Function;
}

export function UserCardSection({
  setLength,
  isLoading,
  setIsLoading,
  setPageNo,
  sortOrder,
  sortType,
  pageNo,
  length,
  regex,
}: props) {
  const [shouldFetch, setShouldFetch] = useState(false);
  const [pageSize, setPageSize] = useState(25);

  const dispatch = useDispatch<any>();
  const creatives = useSelector((state: any) => state.data.creative);
  const adType = useSelector((state: any) => state.data.adType);
  const brand = useSelector((state: any) => state.data.brand);
  const category = useSelector((state: any) => state.data.category);
  const country = useSelector((state: any) => state.data.country);
  const dtLevel = useSelector((state: any) => state.data.dtLevel);
  const feature = useSelector((state: any) => state.data.feature);
  const language = useSelector((state: any) => state.data.language);
  const orientation = useSelector((state: any) => state.data.orientation);
  const region = useSelector((state: any) => state.data.region);
  const stage = useSelector((state: any) => state.data.stage);
  const subBrand = useSelector((state: any) => state.data.subBrand);
  const brandCollab = useSelector((state: any) => state.data.brandCollab);
  const year = useSelector((state: any) => state.data.year);
  const mediaType = useSelector((state: any) => state.data.mediaType) || [];
  const selectedCreativeIds = useSelector((state: any) => state.data.selectedCreativeIds) || [];
  const creativeIdsParam = selectedCreativeIds.length > 0 ? selectedCreativeIds.join(",") : undefined;

  const {
    data: creativeData,
    isLoading: isCreativeLoading,
    isFetching: isCreativeFetching,
    isSuccess: isCreativeSuccess,
  } = useGetCreativeUserQuery({
    title: regex,
    pageSize: pageSize,
    pageNo: pageNo,
    sortType: sortType,
    sortOrder: sortOrder,
    adType: adType?.map((item: item) => item.title) || undefined,
    brand: brand?.map((item: item) => item.title) || undefined,
    category: category?.map((item: item) => item.title) || undefined,
    country: country?.map((item: item) => item.title) || undefined,
    dt: dtLevel?.map((item: item) => item.title) || undefined,
    feature: feature?.map((item: item) => item.title) || undefined,
    language: language?.map((item: item) => item.title) || undefined,
    orientation: orientation?.map((item: item) => item.title) || undefined,
    region: region?.map((item: item) => item.title) || undefined,
    stage: stage?.map((item: item) => item.title) || undefined,
    subBrand: subBrand?.map((item: item) => item.title) || undefined,
    brandCollab: brandCollab?.map((item: item) => item.title) || undefined,
    year: year?.map((item: item) => item.title) || undefined,
    creativeIds: creativeIdsParam,
    // MediaType is separate - pass as comma-separated string
    mediaType: mediaType.length > 0 ? mediaType.join(",") : undefined,
  },
    {
      skip: !shouldFetch
    }
  );

  useEffect(() => {
    // Reset shouldFetch when filters change to trigger a new query
    setShouldFetch(false);

    // Use shorter debounce for filter changes (mediaType, etc.) vs search
    // For search (regex changes), we want longer debounce to avoid too many API calls
    const debounceDelay = regex ? 500 : 100; // Shorter delay for filter changes, longer for search

    const handler = setTimeout(() => {
      setShouldFetch(true); // Set shouldFetch to true after debounce delay
    }, debounceDelay);

    return () => {
      clearTimeout(handler); // Clear timeout if filters change again
    };
  }, [
    adType,
    brand,
    brandCollab,
    category,
    country,
    dtLevel,
    feature,
    language,
    mediaType,
    orientation,
    pageNo,
    pageSize,
    regex,
    region,
    sortOrder,
    sortType,
    stage,
    subBrand,
    year,
    selectedCreativeIds,
  ]);

  function increasePageSize() {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: `Engage_Tap_SeeMore`,
        category: "User Interaction",
        action: "Button Click",
        label: `SeeMore Button`,
      });
    }
    setPageNo((prevPageNo: number) => prevPageNo + 1);
  }

  useEffect(() => {
    if (creativeData && pageNo === 1 && !isCreativeFetching && !isCreativeLoading) {
      setIsLoading(false)
      setShouldFetch(false);
      dispatch(storeCreativeCleanHandler());
      const creativeList = creativeData.data.data?.map(
        (item: CreativeType) => ({
          title: item.newTitle || item.title,
          thumbLink: item.thumbImage,
          videoLink: item.videoLink,
          stage: item?.stage || "",
          dt: item?.dt || "",
          orientation: item?.orientation || "",
          mediaType: item?.mediaType || "",
          imageGallery: item?.imageGallery || [],
          pptDetails: item?.pptDetails || {
            pptLink: "",
            pdfLink: "",
          },
          _id: item._id,
        })
      );
      const data = {
        creative: creativeList,
      };
      dispatch(storeHandler(data));
      setLength(creativeData?.data?.totalCount);
    }
  }, [
    regex,
    creativeData,
    adType,
    brand,
    category,
    country,
    dtLevel,
    feature,
    language,
    mediaType,
    orientation,
    region,
    stage,
    subBrand,
    brandCollab,
    year,
    pageNo,
    isCreativeFetching,
    isCreativeLoading,
    selectedCreativeIds,
  ]);

  useEffect(() => {
    if (isCreativeSuccess && pageNo !== 1 && !isCreativeFetching && !isCreativeLoading) {
      setIsLoading(false)
      setShouldFetch(false);
      console.log("page add");
      const creativeList = creativeData.data.data?.map(
        (item: CreativeType) => ({
          title: item.newTitle || item.title,
          thumbLink: item.thumbImage,
          videoLink: item.videoLink,
          stage: item?.stage || "",
          dt: item?.dt || "",
          orientation: item?.orientation || "",
          mediaType: item?.mediaType || "",
          imageGallery: item?.imageGallery || [],
          pptDetails: item?.pptDetails || {
            pptLink: "",
            pdfLink: "",
          },
        })
      );
      const data = {
        creative: [...creatives, ...creativeList],
      };
      dispatch(storeHandler(data));
      setLength(creativeData?.data.totalCount);
    }
    // console.log(pageNo);
  }, [creativeData, selectedCreativeIds]);

  useEffect(() => {
    if (isCreativeLoading || isCreativeFetching) {
      if (pageNo === 1) {
        setIsLoading(true);
      }
    } else {
      // Clear loading state when query completes (success or failure)
      // This ensures loader doesn't stay forever if query fails or returns empty
      if (pageNo === 1 && !isCreativeLoading && !isCreativeFetching) {
        // Small delay to ensure data processing is complete
        setTimeout(() => {
          setIsLoading(false);
        }, 100);
      }
    }
  }, [isCreativeLoading, isCreativeFetching, pageNo])

  const breakpointColumnsObj = {
    default: 2,
    1100: 2,
    700: 2,
    500: 1,
  };

  return (
    <>
      <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 750: 2, 900: 3 }}>
        <Masonry className="p-4 px-8 min-h-[70vh] relative">
          {isLoading ? (
            <Loader />
          ) : creatives.length !== 0 ? (
            creatives?.map((item: itemSchema, index: number) => (
              <UserProductCard key={index} item={item} />
            ))
          ) : (
            <div className="absolute w-[90%] h-full flex items-center justify-center">
              <div className="flex flex-col items-center justify-center">
                <iframe src="https://lottie.host/embed/1e9daa48-f32d-4038-a6bd-9b541e0c6d65/0uPBH4JAkp.lottie"></iframe>
                <p className="text-gray-500 text-lg font-semibold">
                  No creatives found.
                </p>
                <p className="text-gray-400 mt-1 text-center">
                  Try changing your filters or  search another creative.
                </p>
              </div>
            </div>
          )}
        </Masonry>
      </ResponsiveMasonry>
      {!isCreativeLoading && !isCreativeFetching && length && length !== 0 && length > creatives.length ? (
        <div className="flex justify-center">
          <div className="w-max my-4">
            <ButtonV1 setActive={() => increasePageSize()}>
              Load More {length - creatives?.length}
            </ButtonV1>
          </div>
        </div>
      ) : (
        <div className="flex justify-center">
          {/* <div className="w-max my-4">
            <ButtonV1 color="red" setActive={() => decreasePageSize()}>
              Load Less
            </ButtonV1>
          </div> */}
        </div>
      )}
    </>
  );
}
